/**
 * The tenant-wide default frequency — the "set all" control.
 *
 * It is one setting that moves every event at once, so it sits on the Delivery
 * tab with the other tenant-wide settings. Every row on the Events tab inherits
 * it unless an admin has explicitly pinned that row, so changing it here re-points the whole
 * catalogue in a single write and leaves the pinned rows alone. The count next
 * to it says exactly how many rows that exemption applies to, so the admin can
 * see whether "set all" really did set all.
 *
 * It has one more state than a per-event selector: `''` — no blanket cadence,
 * every un-pinned event using the default shipped with its own template. That
 * is the state a tenant starts in on some installs, and it is a real answer
 * rather than a missing value, so the control offers it by name.
 *
 * Like the master switches, it applies immediately rather than staging into the
 * grid's Save — it is a tenant-level switch, not a per-event edit.
 */
import { Badge } from '../../../ui/badge';
import { Label } from '../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { BellOff, Timer } from 'lucide-react';
import {
  FREQUENCY_HINTS,
  INHERIT_FREQUENCY,
  TENANT_INHERIT_LABEL,
  isEmailSilenced,
  othersNote,
  type FrequencySummary,
  type OneDailyDelivery,
  type OthersDelivery,
  type SetupChoice,
} from './types';
import type { NotificationTenantDefaultFrequency } from '../../../../lib/api';

/** The cadence hints stop being true of events the one daily digest governs. */
const ONE_DAILY_NOTES: Record<'digest' | 'bell', string> = {
  digest: 'Email for these events goes in the daily digest at 07:00.',
  bell: 'These events reach people by bell only while digest emails are off.',
};

interface NotificationDefaultFrequencyProps {
  /** The tenant's blanket cadence, or `''` for "each event's own default". */
  value: NotificationTenantDefaultFrequency;
  choices: SetupChoice[];
  summary: FrequencySummary;
  disabled: boolean;
  /** What baseline routing gives people reached without a named buyer or owner, or `null`. */
  others?: OthersDelivery;
  /** What the one daily digest does to the events following this, or `null`. */
  oneDaily?: OneDailyDelivery;
  onChange: (frequency: NotificationTenantDefaultFrequency) => void;
}

export function NotificationDefaultFrequency({
  value,
  choices,
  summary,
  disabled,
  others = null,
  oneDaily = null,
  onChange,
}: NotificationDefaultFrequencyProps) {
  const unset = value === '';
  const silenced = !unset && isEmailSilenced(value);
  const inherited = summary.total - summary.customised;
  // A Weekly default is never brought forward by the digest.
  const oneDailyNote = oneDaily && value !== 'weekly' ? ONE_DAILY_NOTES[oneDaily] : null;

  const explain = (): string => {
    if (unset) {
      return 'No blanket cadence is set, so every event that has not been '
        + 'given one uses the default it ships with. Pick a cadence to put the '
        + `whole catalogue on the same footing.${oneDailyNote ? ` ${oneDailyNote}` : ''}`;
    }
    if (silenced) {
      return 'Every event without its own setting is bell only — nobody in this '
        + 'tenant is emailed about it. Pin an individual event on the Events tab '
        + 'to email that one anyway.';
    }
    return 'How often events notify unless an event on the Events tab is set to '
      + `something else. ${oneDailyNote ?? `${FREQUENCY_HINTS[value] ?? ''}${othersNote(value, others)}`}`;
  };

  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-4 rounded-lg border p-4 ${
        silenced ? 'border-amber-500/60 bg-amber-500/5' : ''
      }`}
    >
      <div className="space-y-1">
        <Label
          htmlFor="notif-default-frequency"
          className="flex items-center gap-2 font-medium"
        >
          {silenced ? <BellOff className="h-4 w-4" /> : <Timer className="h-4 w-4" />}
          Default frequency
          {summary.total > 0 && !unset && (
            <Badge variant="outline">
              {summary.customised === 0
                ? `all ${summary.total} events`
                : `${inherited} of ${summary.total} events`}
            </Badge>
          )}
        </Label>
        <p className="text-sm text-muted-foreground">{explain()}</p>
        {summary.customised > 0 && (
          <p className="text-sm text-muted-foreground">
            {summary.customised === 1
              ? '1 event has a custom frequency and will not follow this.'
              : `${summary.customised} events have a custom frequency and will `
                + 'not follow this.'}
            {' Reset a row to put it back.'}
          </p>
        )}
      </div>

      <Select
        value={unset ? INHERIT_FREQUENCY : value}
        disabled={disabled}
        onValueChange={(next) =>
          onChange(
            next === INHERIT_FREQUENCY
              ? ''
              : (next as NotificationTenantDefaultFrequency),
          )
        }
      >
        <SelectTrigger
          id="notif-default-frequency"
          aria-label="Default frequency for every event"
          className="w-[210px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT_FREQUENCY}>{TENANT_INHERIT_LABEL}</SelectItem>
          <SelectSeparator />
          {choices.map((choice) => (
            <SelectItem key={choice.value} value={choice.value}>
              {choice.value === 'off' ? `${choice.label} — bell only` : choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
